/**
 * useBoardIdentity — the Kanban screen's board-identity derivation (SE-6491).
 *
 * Lives in a hook rather than inline in `MainContainer` for the same reason
 * `usePlayerPreviewDrawerData` does: that screen sits on its
 * `max-lines-per-function` ceiling, and this derivation carries no screen
 * concerns. The rules it applies — and the four states it distinguishes — are
 * documented on `utils/boardIdentity`.
 *
 * It takes the `useBoardManager` and `useKanbanBoard` results WHOLE rather than
 * unpacked arguments, because those two are exactly what board identity and
 * board readiness are derived from, and the adaptation to the pure resolvers'
 * vocabulary belongs here, not at the call site.
 * @module features/player-kanban/hooks/useBoardIdentity
 */

import {
  boardIdForQueries,
  resolveBoardIdentity,
  shouldShowBoardSkeleton,
  type BoardContentState,
  type BoardIdentity,
} from "../utils/boardIdentity";

/** The part of `useBoardManager`'s result that board identity is derived from. */
export type BoardManagerIdentityState = {
  /** The board-list query is in flight (or a board CRUD op is running). */
  readonly isLoading: boolean;
  /** The resolved board list; `undefined` until the first response lands. */
  readonly boards: readonly { readonly id: string }[] | undefined;
  /** The board the screen has activated, if any. */
  readonly activeBoard: { readonly id: string } | null | undefined;
  /** The board-list error message, if the read failed. */
  readonly error: string | null | undefined;
};

/**
 * The part of `useKanbanBoard`'s result that the skeleton decision needs.
 *
 * SE-5630 added the second field. The first is an AGGREGATE over five reads,
 * so on its own it cannot tell a cold start from a background refresh — and
 * the gate that read only it unmounted the whole board surface every time a
 * secondary query went back in flight.
 */
export type BoardContentIdentityState = {
  /** Any query feeding the open board's content is in flight. */
  readonly isLoading: boolean;
  /** The board id whose phases and cards are currently in hand. */
  readonly loadedContentBoardId: string | null | undefined;
};

/** What the screen needs in order to render the right thing. */
export type UseBoardIdentityResult = {
  /** Which board is open, or why none is. */
  readonly boardIdentity: BoardIdentity;
  /**
   * The board id safe to send to the server, or `undefined`. The three id-less
   * states all yield `undefined`, which every downstream `skip: !boardId`
   * guard reads as "do not ask" — no placeholder is ever put on the wire.
   */
  readonly queryBoardId: string | undefined;
  /** Whether the skeleton, rather than the board, should render. */
  readonly showSkeleton: boolean;
};

/**
 * Derive the board identity and the two things the screen reads off it.
 *
 * Not memoised: nothing identity-sensitive consumes the object — a string, a
 * boolean and one `kind` comparison are all that leave this hook — so a fresh
 * object per render costs nothing and a dependency array would be one more
 * thing to keep correct.
 * @param boardManager - The `useBoardManager` result.
 * @param boardContent - The `useKanbanBoard` result.
 * @returns The identity plus the query id and skeleton decision derived from it.
 */
export const useBoardIdentity = (
  boardManager: BoardManagerIdentityState,
  boardContent: BoardContentIdentityState
): UseBoardIdentityResult => {
  const boardIdentity = resolveBoardIdentity({
    isLoadingBoards: boardManager.isLoading,
    boards: boardManager.boards,
    activeBoard: boardManager.activeBoard,
    boardsError: boardManager.error,
  });

  const content: BoardContentState = {
    isLoadingBoardContent: boardContent.isLoading,
    loadedContentBoardId: boardContent.loadedContentBoardId,
  };

  return {
    boardIdentity,
    queryBoardId: boardIdForQueries(boardIdentity),
    showSkeleton: shouldShowBoardSkeleton(boardIdentity, content),
  };
};
